import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, map, Observable, shareReplay, switchMap, take, tap } from 'rxjs';
import {
  GamesInSingleDayModel,
  GamesListQueryHandler,
  GamesListQueryResult,
  SetFilterCommandHandler
} from '../../application/handlers';
import {
  AnalyticsService,
  FindGamesAnalyticsEvent,
  GameDetailsModel,
  isPrivateGame,
  LocationAccessService,
  LocationAnalyticsData,
  LocationStorage,
  MODAL_TOKEN,
  ModalProvider,
  SelectedLocation,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { NgClass } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { FiltersComponent } from '../filters/filters.component';
import { BadgeModeEnum, GameCardV2Component, LocationInfoBoxComponent } from '@ui-components';
import { GameDetailsModalComponent } from '@games';
import { UserRoleEnum, Visibility } from '@core-http';
import { toObservable } from '@angular/core/rxjs-interop';
import { DistanceFilterStorage } from '@find-games/infra';

const LOCATION_BOX_AFTER_GAME: number = 5;

@Component({
  selector: 'lib-games-list',
  templateUrl: './games-list.component.html',
  styleUrls: ['./games-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [IonicModule, FiltersComponent, NgClass, GameCardV2Component, LocationInfoBoxComponent],
  providers: [GamesListQueryHandler, SetFilterCommandHandler]
})
export class GamesListComponent {
  private readonly gamesListQueryHandler: GamesListQueryHandler = inject(GamesListQueryHandler);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly locationStorage: LocationStorage = inject(LocationStorage);
  private readonly distanceFilterStorage: DistanceFilterStorage = inject(DistanceFilterStorage);
  private readonly setFilterCommandHandler: SetFilterCommandHandler = inject(SetFilterCommandHandler);
  private readonly locationAccessService: LocationAccessService = inject(LocationAccessService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  readonly badgeMode: typeof BadgeModeEnum = BadgeModeEnum;

  private readonly showPrivate: WritableSignal<boolean> = signal<boolean>(false);

  private readonly isAdmin$: Observable<boolean> = this.userDataProvider.userData$.pipe(
    map((userData: UserModel) => userData.role === UserRoleEnum.ADMIN)
  );

  readonly isAdmin: Signal<boolean | undefined> = toSignal(this.isAdmin$);

  private readonly privateAccess$: Observable<boolean> = combineLatest([
    toObservable(this.showPrivate),
    this.isAdmin$
  ]).pipe(map(([showPrivate, isAdmin]: [boolean, boolean]) => showPrivate && isAdmin));

  private readonly games$: Observable<GamesListQueryResult> = this.modalProvider.showLoading$().pipe(
    switchMap(() => this.privateAccess$),
    switchMap((showPrivate: boolean) => this.gamesListQueryHandler.getGames(showPrivate)),
    tap(() => this.modalProvider.dismissLoading$()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly games: Signal<GamesListQueryResult | undefined> = toSignal(this.games$);

  readonly gamesCount: Signal<{ totalGames: number; privateGames: number } | undefined> = computed(() => {
    const gamesList: GamesListQueryResult | undefined = this.games();
    if (!gamesList) return undefined;

    const allGames: GameDetailsModel[] = gamesList.day?.flatMap((day: GamesInSingleDayModel) => day.games) || [];
    const privateGames: GameDetailsModel[] = allGames.filter(
      (game: GameDetailsModel) => game.visibility === Visibility.PRIVATE
    );

    return {
      totalGames: allGames.length,
      privateGames: privateGames.length
    };
  });

  readonly gamesBeforeDay: Signal<number[]> = computed(() => {
    const days: GamesInSingleDayModel[] = this.games()?.day ?? [];
    const offsets: number[] = [];
    let total: number = 0;

    days.forEach((day: GamesInSingleDayModel) => {
      offsets.push(total);
      total += day.games.length;
    });

    return offsets;
  });

  readonly locationBoxPosition: Signal<number> = computed(() => {
    const totalGames: number = this.gamesCount()?.totalGames ?? 0;

    return Math.min(LOCATION_BOX_AFTER_GAME, totalGames) - 1;
  });

  setShowPrivateGames(value: boolean): void {
    this.showPrivate.set(value);
  }

  isLocationBoxPosition(dayIndex: number, gameIndex: number): boolean {
    return this.gamesBeforeDay()[dayIndex] + gameIndex === this.locationBoxPosition();
  }

  onLocationBoxClicked(): void {
    const gamesCount: number = this.gamesCount()?.totalGames ?? 0;

    this.locationAccessService
      .accessState$()
      .pipe(take(1))
      .subscribe((state: LocationAnalyticsData) =>
        this.analyticsService.buttonClick(FindGamesAnalyticsEvent.ListInfoBoxLocation, { ...state, gamesCount })
      );
  }

  onLocationAcquired(location: SelectedLocation): void {
    this.locationStorage.setLocation(location).pipe(take(1)).subscribe();

    this.setFilterCommandHandler
      .set({
        latitude: location.coordinates.latitude,
        longitude: location.coordinates.longitude,
        maxDistanceMeters: this.distanceFilterStorage.selectedAsFilterValue
      })
      .pipe(take(1))
      .subscribe();
  }

  openDetailsModal(game: GameDetailsModel): void {
    if (isPrivateGame(game) && !this.isAdmin()) return;

    this.userDataProvider.userData$
      .pipe(
        take(1),
        switchMap((userData: UserModel) =>
          this.modalProvider.showModal$({
            component: GameDetailsModalComponent,
            componentProps: {
              gameId: game.gameId,
              adminAccess: userData.role === UserRoleEnum.ADMIN
            },
            cssClass: 'modal-auto',
            initialBreakpoint: 1,
            breakpoints: [0, 1]
          })
        )
      )
      .subscribe();
  }
}
